/**
 * prosemirror-tables 패키지의 cellSelection 을 쓰려면 tableMap을 써야하는데 tableMap은 직사각형 형태의 테이블을 가정하고 있어서 오류가 발생함
 * 따라서 직사각형 형태의 테이블을 가정하지 않는 코드를 작성하기 위한 cell resize를 돕는 유틸리티들
 */
import { type DOMOutputSpec, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { CellSelection } from '../pm-table-plugin';
import { convertToPt } from '../../extension-word/utils';

export interface CellInfo {
    pos: number;
    colspan: number;
    rowspan: number;
    colStart: number;
    colEnd: number;
    rowStart: number;
    rowEnd: number;
    width: string;
}

export interface TableStructure {
    cells: CellInfo[];
    rowCount: number;
    maxCols: number;
    colgroup: DOMOutputSpec[];
    rowWidths: number[];
    totalWidth: number;
}

// 테이블 구조를 캐싱하기 위한 WeakMap
const tableStructureCache = new WeakMap<ProseMirrorNode, TableStructure>();

export function analyzeTableStructure(tableNode: ProseMirrorNode, tablePos: number): TableStructure {
    // 캐시에서 테이블 구조를 확인
    const cachedStructure = tableStructureCache.get(tableNode);
    if (tablePos !== 0 && cachedStructure) {
        return cachedStructure;
    }

    const cells: CellInfo[] = [];
    let rowCount = 0;
    let maxCols = 0;
    let currentRow = 0;
    let currentCol = 0;
    let pos = tablePos;
    const colgroup: DOMOutputSpec[] = [];

    // 이미 사용된 셀 위치를 추적하기 위한 맵
    const occupiedPositions = new Map<string, boolean>();
    // 각 열의 너비를 저장하기 위한 맵
    const columnWidths = new Map<number, string>();

    tableNode.forEach(row => {
        currentCol = 0;
        rowCount++;
        pos++; // row 시작 태그

        // 이전 행에서 확장된 rowspan 셀들의 공간 건너뛰기
        while (occupiedPositions.get(`${currentRow},${currentCol}`)) {
            currentCol++;
        }

        row.forEach(cell => {
            // 이미 사용중인 위치 건너뛰기
            while (occupiedPositions.get(`${currentRow},${currentCol}`)) {
                currentCol++;
            }

            const colspan = cell.attrs.colspan || 1;
            const rowspan = cell.attrs.rowspan || 1;
            const cellWidth = `${cell.attrs.cellWidth?.value || 0}pt`;

            // 현재 셀의 위치 정보 저장
            cells.push({
                pos: pos + 1, // 현재 셀의 실제 위치
                colspan,
                rowspan,
                colStart: currentCol,
                colEnd: currentCol + colspan - 1,
                rowStart: currentRow,
                rowEnd: currentRow + rowspan - 1,
                width: cellWidth,
            });

            // colspan이 1인 경우 해당 열의 너비를 직접 저장
            if (colspan === 1) {
                columnWidths.set(currentCol, cellWidth);
            }

            // 셀이 차지하는 모든 위치를 occupied로 표시
            for (let r = 0; r < rowspan; r++) {
                for (let c = 0; c < colspan; c++) {
                    occupiedPositions.set(`${currentRow + r},${currentCol + c}`, true);
                }
            }

            pos += cell.nodeSize; // 다음 셀 위치로 이동
            currentCol += colspan;
        });

        pos++; // row 종료 태그
        maxCols = Math.max(maxCols, currentCol);
        currentRow++;
    });

    // colgroup 생성
    for (let i = 0; i < maxCols; i++) {
        const width = columnWidths.get(i);
        colgroup.push(['col', { width: width || null }]);
    }

    // 각 행별 너비 계산
    const rowWidths = new Map<number, number>();
    for (let row = 0; row < rowCount; row++) {
        let rowWidth = 0;
        cells.forEach(cell => {
            if (cell.rowStart <= row && cell.rowEnd >= row) {
                rowWidth += convertToPt(cell.width || '0pt');
            }
        });
        rowWidths.set(row, rowWidth);
    }

    // 가장 큰 너비를 totalWidth로 지정
    const totalWidth = Math.max(...Array.from(rowWidths.values()));

    // colgroup 이 비어있는 열이 있을때 채워줌
    const filledColgroup = fillEmptyColgroup(colgroup, cells, totalWidth);

    const structure = { cells, rowCount, maxCols, colgroup: filledColgroup, rowWidths: Array.from(rowWidths.values()), totalWidth };
    // 캐시에 테이블 구조 저장
    if (tablePos !== 0) {
        tableStructureCache.set(tableNode, structure);
    }

    return structure;
}

// 비어있는 colgroup 을 찾아서 채워줌
function fillEmptyColgroup(originalColgroup: DOMOutputSpec[], cells: CellInfo[], totalWidth: number): DOMOutputSpec[] {
    // colgroup 복사
    const colgroup = originalColgroup.map(col => ['col', { ...col[1] }] as DOMOutputSpec);

    // colgroup 에서 width가 0pt 인 열을 찾아서 채워줌
    for (let i = 0; i < colgroup.length; i++) {
        if (colgroup[i][1].width === null) {
            // 해당 열을 포함하는 모든 셀들을 찾음
            const spanningCells = cells.filter(cell => cell.colStart <= i && cell.colEnd >= i && cell.width !== null);

            // 해당 셀로부터 이전셀들과 이후셀들의 합을 제외시켜서 빈값 채움
            for (const cell of spanningCells) {
                const prevColgroup = colgroup.slice(cell.colStart, i);
                const nextColgroup = colgroup.slice(i + 1, cell.colEnd + 1);
                const hasAllPrevColgroupWidth = prevColgroup.length === 0 || prevColgroup.every(col => col[1].width !== null);
                const hasAllNextColgroupWidth = nextColgroup.length === 0 || nextColgroup.every(col => col[1].width !== null);
                if (hasAllPrevColgroupWidth && hasAllNextColgroupWidth) {
                    const prevColgroupWidth = prevColgroup.reduce((acc, col) => acc + convertToPt(col[1].width), 0);
                    const nextColgroupWidth = nextColgroup.reduce((acc, col) => acc + convertToPt(col[1].width), 0);
                    const cellWidth = convertToPt(cell.width) - prevColgroupWidth - nextColgroupWidth;
                    if (cellWidth > 0) {
                        colgroup[i][1].width = `${cellWidth}pt`;
                        break;
                    }
                }
            }
        }
    }

    // colgroup 이 두개이상 연속으로 null 일때 해당하는 열과 갯수만큼의 colspan을 가진 셀이 있다면 해당셀의 너비를 나누어서 적용
    for (let i = 0; i < colgroup.length; i++) {
        if (colgroup[i][1].width === null) {
            let consecutiveZeroCols = 1;
            let j = i + 1;
            while (j < colgroup.length && colgroup[j][1].width === null) {
                consecutiveZeroCols++;
                j++;
            }

            if (consecutiveZeroCols > 1) {
                // 해당 열 범위를 포함하는 colspan이 있는 셀 찾기
                const spanningCell = cells.find(cell => cell.colStart === i && cell.colEnd === i + consecutiveZeroCols - 1 && cell.colspan === consecutiveZeroCols && cell.width !== null);

                if (spanningCell) {
                    const cellWidth = convertToPt(spanningCell.width);
                    const widthPerCol = cellWidth / consecutiveZeroCols;

                    // 연속된 0pt 열들에 너비 분배
                    for (let k = 0; k < consecutiveZeroCols; k++) {
                        colgroup[i + k][1].width = `${widthPerCol}pt`;
                    }
                }
            }
        }
    }

    // colgroup 이 한개만 비어있다면 totalWidth 에서 나머지를 빼서 채워줌
    let emptyColgroupCount = 0;
    let emptyColgroupIndex = 0;
    for (let i = 0; i < colgroup.length; i++) {
        if (colgroup[i][1].width === null) {
            emptyColgroupCount++;
            emptyColgroupIndex = i;
        }
    }

    if (emptyColgroupCount === 1) {
        const width = totalWidth - colgroup.reduce((acc, col) => acc + convertToPt(col[1].width || '0pt'), 0);
        colgroup[emptyColgroupIndex][1].width = `${width}pt`;
    }

    return colgroup;
}

export function isCellSelection(value: unknown): value is CellSelection {
    return value instanceof CellSelection;
}

/**
 * 주어진 cell의 실제 열 INDEX를 찾습니다.
 * colspan과 rowspan을 고려하여 정확한 열 INDEX를 계산합니다.
 *
 * @param doc ProseMirror 문서
 * @param cellPos 찾고자 하는 cell의 위치
 * @returns 실제 열 INDEX (0부터 시작) | null
 */
export function findActualColumnInfo(doc: ProseMirrorNode, cellPos: number): { targetCell: CellInfo | null; tableStructure: TableStructure } {
    const $cell = doc.resolve(cellPos + 1);
    const tableNode = $cell.node(-2);
    const tablePos = $cell.before(-2);

    const structure = analyzeTableStructure(tableNode, tablePos);
    const targetCell = structure.cells.find(cell => cell.pos === cellPos);

    return { targetCell: targetCell || null, tableStructure: structure };
}
